import { CreativeJob } from "api/creativeStudio";
import DSBox from "views/system-design/DSBox";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSChip from "views/system-design/DSChip";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSSectionHeading from "views/system-design/DSSectionHeading";
import DSTooltip from "views/system-design/DSTooltip";
import DSTypography from "views/system-design/DSTypography";
import sectionCardSx from "views/system-design/sectionCardSx";
import {
  DSTable,
  DSTableBody,
  DSTableCell,
  DSTableContainer,
  DSTableHead,
  DSTableRow,
} from "views/system-design/Table";
import {
  STATUS_COLORS,
  describeOutput,
  formatUsd,
} from "../utils/creativeOptions";
import CreativeJobOutputs from "./CreativeJobOutputs";

const SHORT_DESCRIPTION = 90;

const formatDate = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "-";

const shorten = (text: string) =>
  text.length > SHORT_DESCRIPTION
    ? `${text.slice(0, SHORT_DESCRIPTION).trimEnd()}…`
    : text;

type Props = {
  jobs: CreativeJob[];
  isLoading: boolean;
  errorMessage?: string;
};

const CreativeJobsTable = ({ jobs, isLoading, errorMessage }: Props) => (
  <DSCard sx={sectionCardSx}>
    <DSSectionHeading>Recent creatives</DSSectionHeading>
    <DSCardContent>
      {isLoading && (
        <DSBox sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <DSCircularProgress />
        </DSBox>
      )}
      {!isLoading && errorMessage && (
        <DSTypography color="error">{errorMessage}</DSTypography>
      )}
      {!isLoading && !errorMessage && jobs.length === 0 && (
        <DSTypography color="text.secondary">
          No creatives yet. Your first one will show up here.
        </DSTypography>
      )}
      {jobs.length > 0 && (
        <DSTableContainer>
          <DSTable size="small">
            <DSTableHead>
              <DSTableRow>
                <DSTableCell>Created</DSTableCell>
                <DSTableCell>By</DSTableCell>
                <DSTableCell>Description</DSTableCell>
                <DSTableCell>Output</DSTableCell>
                <DSTableCell>Status</DSTableCell>
                <DSTableCell>Progress</DSTableCell>
                <DSTableCell align="right">Cost</DSTableCell>
                <DSTableCell>Results</DSTableCell>
              </DSTableRow>
            </DSTableHead>
            <DSTableBody>
              {jobs.map((job) => {
                const description = job.request?.description ?? "";
                return (
                  <DSTableRow key={job.id} sx={{ verticalAlign: "top" }}>
                    <DSTableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDate(job.createdAt)}
                    </DSTableCell>
                    <DSTableCell sx={{ wordBreak: "break-word" }}>
                      {job.requestedBy?.email || job.requestedBy?.uid || "-"}
                    </DSTableCell>
                    <DSTableCell sx={{ minWidth: 200, maxWidth: 320 }}>
                      <DSTooltip title={description}>
                        <span>{shorten(description)}</span>
                      </DSTooltip>
                    </DSTableCell>
                    <DSTableCell sx={{ whiteSpace: "nowrap" }}>
                      {job.request?.output
                        ? describeOutput(job.request.output)
                        : "-"}
                    </DSTableCell>
                    <DSTableCell>
                      <DSChip
                        size="small"
                        label={job.status}
                        color={STATUS_COLORS[job.status] ?? "default"}
                      />
                    </DSTableCell>
                    <DSTableCell sx={{ maxWidth: 260 }}>
                      {job.status === "failed" ? (
                        <DSTypography
                          sx={{ color: "error.main", fontSize: "12px" }}
                        >
                          {job.error || "Failed"}
                        </DSTypography>
                      ) : (
                        <>
                          <DSTypography sx={{ fontSize: "12px" }}>
                            {job.progress || "-"}
                          </DSTypography>
                          {job.warnings?.map((warning) => (
                            <DSTypography
                              key={warning}
                              sx={{ color: "warning.main", fontSize: "12px" }}
                            >
                              {warning}
                            </DSTypography>
                          ))}
                        </>
                      )}
                    </DSTableCell>
                    <DSTableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {typeof job.costUsd === "number"
                        ? formatUsd(job.costUsd)
                        : "-"}
                    </DSTableCell>
                    <DSTableCell>
                      <CreativeJobOutputs job={job} />
                    </DSTableCell>
                  </DSTableRow>
                );
              })}
            </DSTableBody>
          </DSTable>
        </DSTableContainer>
      )}
    </DSCardContent>
  </DSCard>
);

export default CreativeJobsTable;
